import type { BaseMembers } from "@auth0/auth0-acul-js";

/**
 * Retrieves an extension parameter from the SDK's untrustedData.authorizationParams.
 * Extension parameters must be prefixed with "ext-" in the authorize URL.
 *
 * @example
 * // In your application's authorize call:
 * await auth0.loginWithRedirect({
 *   authorizationParams: {
 *     'ext-use-mobile': 'true'
 *   }
 * });
 *
 * In your ACUL screen:
 * const useMobile = getExtParam(signupInstance, 'ext-use-mobile');
 *
 * @param instance - The SDK screen instance (e.g., SignupInstance, LoginInstance)
 * @param key - The parameter key (must be prefixed with "ext-")
 * @returns The parameter value or undefined if not present
 */
export function getExtParam(
  instance: BaseMembers | null | undefined,
  key: `ext-${string}`
): string | undefined {
  return instance?.untrustedData?.authorizationParams?.[key];
}

/**
 * Checks if the "ext-use-mobile" parameter is set to "true".
 * Used to determine if the user is accessing from a mobile app context.
 *
 * @example
 * // In your application's authorize call:
 * await auth0.loginWithRedirect({
 *   authorizationParams: {
 *     'ext-use-mobile': 'true'
 *   }
 * });
 *
 * @param instance - The SDK screen instance
 * @returns true if ext-use-mobile is "true", false otherwise
 */
export function isUseMobile(instance: BaseMembers | null | undefined): boolean {
  return getExtParam(instance, "ext-use-mobile") === "true";
}

/**
 * Set of client IDs that should NOT display social login buttons.
 * Add the Auth0 client IDs for applications where Google, Apple,
 * and other social connections should be hidden.
 *
 * Exported so that tests can add/remove entries for verification.
 */
export const CLIENTS_WITHOUT_SOCIAL = new Set<string>([
  // Add your client IDs here, e.g.:
  // "abc123def456ghi789jkl012mno345pq",
]);

/**
 * Checks if all social connections should be hidden for this request.
 * Returns true if either:
 * - The "ext-hide-social" authorization param is set to "true", OR
 * - The client ID is in the CLIENTS_WITHOUT_SOCIAL list
 *
 * @example
 * // Approach 1: Pass ext param from your application's authorize call:
 * await auth0.loginWithRedirect({
 *   authorizationParams: {
 *     'ext-hide-social': 'true'
 *   }
 * });
 *
 * // Approach 2: Add the client ID to CLIENTS_WITHOUT_SOCIAL array above.
 *
 * // In your ACUL screen:
 * if (shouldHideSocialConnections(loginInstance)) {
 *   // Don't render social buttons
 * }
 *
 * @param instance - The SDK screen instance (e.g., LoginInstance, SignupInstance)
 * @returns true if social connections should be hidden, false otherwise
 */
export function shouldHideSocialConnections(
  instance: BaseMembers | null | undefined
): boolean {
  if (getExtParam(instance, "ext-hide-social") === "true") {
    return true;
  }

  const clientId = instance?.client?.id;

  return clientId ? CLIENTS_WITHOUT_SOCIAL.has(clientId) : false;
}
